import {useEffect, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import useAuth from '../auth/useAuth';
import {createNotificationSession, InboxSnapshot} from './notificationSession';
import {defaultNotificationState, NotificationState} from './notificationState';

// Kept for consumers of the pre-inbox application context.
export interface Notification {
  notificationId: string;
  title?: string;
  body: string;
  icon?: string;
  badgeIcon?: string;
  image?: string;
  [key: string]: any;
}

export type {NotificationState} from './notificationState';

const useNotification = (): NotificationState => {
  const {user, isAuthenticated, token, expiresAt} = useAuth();
  const queryClient = useQueryClient();
  const identity = isAuthenticated && user
    ? JSON.stringify([user.id ?? user.externalId ?? null, user.username]) : null;
  const expiration = expiresAt?.getTime() ?? 0;
  const current = useRef({identity, token});
  current.current = {identity, token};
  const [inbox, setInbox] = useState<{identity: string | null; snapshot: InboxSnapshot}>({
    identity: null, snapshot: {state: defaultNotificationState, cursor: null},
  });
  const latest = useRef(inbox);
  latest.current = inbox;

  useEffect(() => {
    const isCurrent = () => current.current.identity === identity && current.current.token === token;
    if (!identity) {
      setInbox({identity: null, snapshot: {state: defaultNotificationState, cursor: null}});
      return;
    }
    const seed = latest.current.identity === identity ? latest.current.snapshot : undefined;
    if (!token || !Number.isFinite(expiration) || expiration <= Date.now()) {
      setInbox({identity, snapshot: {
        state: {...(seed?.state || defaultNotificationState), connectionStatus: 'offline',
          loading: false, loadingMore: false, markingRead: false},
        cursor: seed?.cursor ?? null,
      }});
      return;
    }
    const session = createNotificationSession({
      token,
      expiresAt: expiration,
      seed,
      isCurrent,
      publish: snapshot => {
        if (isCurrent()) setInbox({identity, snapshot});
      },
      invalidate: () => {
        for (const key of ['groups', 'groupDetails', 'receiptDetails', 'history', 'userStatistics', 'getStatistics']) {
          void queryClient.invalidateQueries([key]).catch(() => {});
        }
      },
    });
    return () => session.dispose();
  }, [identity, token, expiration, queryClient]);

  // Never expose the previous identity's data, including the render before effect cleanup.
  if (!identity || inbox.identity !== identity) return defaultNotificationState;
  return inbox.snapshot.state;
};

export default useNotification;
